import { CheckIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spacer,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ViewState } from "react-map-gl/maplibre";
import { useSearch } from "wouter";

import { getBulkExports, useFrontendSettings, useMultipleScenarios } from "@nens/ldo/api-v1";
import { Breadcrumbs, ComparisonMap, ComparisonTable } from "@nens/ldo/components";
import { useDelayedHoverLocation } from "@nens/ldo/hooks";
import { useLocalStorage } from "@nens/react/hooks";
import { useMaptilerStyleJson } from "@nens/react-map-gl/utils";
import { flatten, makeUnique, removeNullUndefined } from "@nens/shared-utils";

import { AddToExportModal } from "./add-to-export-modal";

const MAP_SIZES = [300, 400, 500, 600];

export default function ScenarioComparisonPage() {
  const searchParams = new URLSearchParams(useSearch());
  const intl = useIntl();

  const idParam = searchParams.get("ids");
  const scenarioIds = idParam
    ? (searchParams.get("ids") ?? "").split(",").map((id) => Number.parseInt(id))
    : [];

  const [disabledScenarios, setDisabledScenarios] = useState<Record<number, boolean>>({});

  const [viewState, setViewState] = useState<ViewState>({
    longitude: 5.4612108,
    latitude: 52,
    zoom: 5.5,
    pitch: 0,
    bearing: 0,
    padding: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
  });

  const frontendSettings = useFrontendSettings();
  const style = useMaptilerStyleJson(
    "basic",
    frontendSettings.isSuccess ? frontendSettings.data.maptiler_api_key : null,
    false
  );

  const scenariosResponse = useMultipleScenarios(scenarioIds);

  // Table fields are configurable
  const [fields, setFields] = useLocalStorage<string[]>("ldo_comparison_fields", [
    "projectnaam",
    "overschrijdingsfrequentie",
  ]);
  const aScenario = scenariosResponse.find((response) => response.data)?.data;
  const keysAndTitles = aScenario ? aScenario.getAllPropertyKeysAndTitles() : null;

  const rasterTypes = makeUnique(
    flatten(
      removeNullUndefined(
        scenariosResponse.map((response) =>
          response.isSuccess ? response.data.rasters.map((raster) => raster.type) : null
        )
      )
    )
  );

  const [rasterType, setRasterType] = useState("max_waterdepth");
  const [mapSizePx, setMapSizePx] = useState(300);

  // Hovering on any map sets currentLocation for the whole page
  const { onHover, onMouseOut, currentLocation } = useDelayedHoverLocation();
  const { onOpen, onClose, isOpen } = useDisclosure();

  const scenariosToExport = removeNullUndefined(
    scenariosResponse
      .filter((response) => response.data)
      .map((response) => response.data)
      .filter((scenario) => scenario && !disabledScenarios[scenario.id])
  );
  const someNotExportable = scenariosToExport.some((scenario) => !scenario.canBeExported);
  const scenarioIdsToExport = scenariosToExport.map((scenario) => "" + scenario.id);

  const bulkExportsResponse = useQuery(getBulkExports("all", { status: "incomplete" }));
  const bulkExports = bulkExportsResponse.data?.items ?? [];

  return (
    <Box w="100%">
      <Box w="100%">
        <Breadcrumbs
          crumbs={[
            { label: intl.formatMessage({ id: "pages.home", defaultMessage: "Home" }), href: "/" },
            {
              label: intl.formatMessage({ id: "pages.scenarios", defaultMessage: "Scenarios" }),
              href: "/scenarios",
            },
            {
              label: intl.formatMessage({
                id: "pages.scenarios.compare",
                defaultMessage: "Scenario comparison",
              }),
              href: "/scenarios/compare",
            },
          ]}
        />
      </Box>
      <Container maxW={"7xl"} py={"24px"} justifyItems="center">
        <Heading mb={4} size="lg">
          <FormattedMessage
            id="pages.scenarios.compare.header"
            defaultMessage="Compare {num} scenarios"
            values={{ num: scenarioIds.length }}
          />
          <Menu>
            <MenuButton as={Button} ml={4} leftIcon={<SettingsIcon />}>
              <FormattedMessage
                id="pages.scenario.compare.columns_button"
                defaultMessage="Columns"
              />
            </MenuButton>
            <MenuList maxHeight="600px" overflowY="scroll">
              {keysAndTitles &&
                Object.keys(keysAndTitles)
                  .sort()
                  .map((key) => (
                    <MenuItem
                      fontSize="sm"
                      key={key}
                      onClick={() =>
                        fields.includes(key)
                          ? setFields(fields.filter((f) => f !== key))
                          : setFields([...fields, key])
                      }
                    >
                      {keysAndTitles[key] ?? "-"}
                      {fields.includes(key) ? <CheckIcon ml={2} /> : null}
                    </MenuItem>
                  ))}
            </MenuList>
          </Menu>
          <Button
            ml={4}
            isDisabled={
              bulkExports.length === 0 || someNotExportable || scenariosToExport.length === 0
            }
            title={
              someNotExportable
                ? intl.formatMessage({
                    id: "pages.scenario.compare.scenarios_not_exportable",
                    defaultMessage: "Some scenarios have a status that cannot be exported",
                  })
                : intl.formatMessage({
                    id: "pages.scenario.compare.add_to_export",
                    defaultMessage: "Add selected scenario's to an existing bulk export",
                  })
            }
            onClick={onOpen}
          >
            <FormattedMessage
              id="pages.scenario.compare.add_to_export_2"
              defaultMessage="Add selected to export"
            />
          </Button>
          <AddToExportModal
            scenarioIds={scenarioIdsToExport}
            onClose={onClose}
            isOpen={isOpen}
            bulkExports={bulkExports}
          />
        </Heading>
        <ComparisonTable
          scenariosResponse={scenariosResponse}
          fields={fields}
          fieldTitles={keysAndTitles}
          disabledScenarios={disabledScenarios}
          setDisabledScenarios={setDisabledScenarios}
        />
        <Flex w="100%" gap={4} p={4}>
          <Box>
            <label>
              <FormattedMessage
                id="pages.scenario.compare.select_raster"
                defaultMessage="Select a raster type:"
              />
            </label>
            <Select value={rasterType} onChange={(e) => setRasterType(e.target.value)}>
              {rasterTypes.map((raster) => (
                <option value={raster} key={raster}>
                  {raster}
                </option>
              ))}
            </Select>
          </Box>
          <Box>
            <label>
              <FormattedMessage
                id="pages.scenario.compare.map_size"
                defaultMessage="Select the map size:"
              />
            </label>
            <Select
              value={mapSizePx}
              onChange={(e) => setMapSizePx(Number.parseInt(e.target.value))}
            >
              {MAP_SIZES.map((size) => (
                <option value={size} key={size}>
                  {size}
                </option>
              ))}
            </Select>
          </Box>
          <Spacer />
        </Flex>
        <Flex w="100%" gap={4} flexWrap="wrap" m={4}>
          {scenarioIds.map(
            (id, idx) =>
              !disabledScenarios[id] && (
                <ComparisonMap
                  mapStyle={style ?? undefined}
                  viewState={viewState}
                  setViewState={setViewState}
                  key={id}
                  scenario={scenariosResponse[idx]?.data ?? null}
                  rasterType={rasterType}
                  sizePx={mapSizePx}
                  onHover={onHover}
                  onMouseOut={onMouseOut}
                  currentLocation={currentLocation}
                />
              )
          )}
        </Flex>
      </Container>
    </Box>
  );
}
